import { useState, useRef, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Phone, Loader2, ChevronsUpDown, Check } from "lucide-react";
import { parsePhoneNumber, isValidPhoneNumber, CountryCode, getCountryCallingCode, getCountries } from "libphonenumber-js";
import { getFlagSvgUrl, iso2ToFlagEmoji } from "@/lib/country-flags";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

// Complete supported ISO 3166-1 alpha-2 country list from libphonenumber-js.
const ALL_COUNTRY_CODES: CountryCode[] = getCountries();

const POPULAR_CODES = new Set<CountryCode>([
  'AU','US','GB','NZ','CA','KE','NG','ET','SS','UG','TZ','SO','SD','GH','ZA'
]);

interface CountryOption {
  code: CountryCode;
  dialCode: string;
  name: string;
}

// Build country options once
const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });

function buildCountryOptions(): CountryOption[] {
  const options: CountryOption[] = [];
  for (const code of ALL_COUNTRY_CODES) {
    try {
      const dialCode = getCountryCallingCode(code);
      const name = displayNames.of(code) || code;
      options.push({ code, dialCode, name });
    } catch {
      // Skip codes without calling codes
    }
  }
  return options.sort((a, b) => a.name.localeCompare(b.name));
}

const ALL_COUNTRIES = buildCountryOptions();
const POPULAR_COUNTRIES = ALL_COUNTRIES.filter(c => POPULAR_CODES.has(c.code));
const OTHER_COUNTRIES = ALL_COUNTRIES.filter(c => !POPULAR_CODES.has(c.code));

function normalizePhoneForCountry(input: string, country: CountryCode): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  try {
    const parsed = parsePhoneNumber(trimmed, country);
    if (parsed?.isValid()) return parsed.format("E.164");
  } catch { /* try AU mobile shorthand below */ }

  // libphonenumber expects AU mobile national input as 04XXXXXXXX. Users often
  // type the same local mobile without the trunk zero: 4XXXXXXXX.
  if (country === 'AU' && /^4\d{8}$/.test(trimmed.replace(/\D/g, ""))) {
    try {
      const parsed = parsePhoneNumber(`0${trimmed.replace(/\D/g, "")}`, country);
      if (parsed?.isValid()) return parsed.format("E.164");
    } catch { /* fall through */ }
  }

  return null;
}

interface PhoneAuthInputProps {
  onSubmit: (fullPhoneNumber: string, channel?: 'sms' | 'whatsapp') => Promise<void>;
  isLoading: boolean;
  mode?: 'login' | 'signup';
  buttonText?: string;
  /** Show 'Verify with WhatsApp' alternative under primary CTA. */
  showWhatsAppOption?: boolean;
  /** Optional E.164 phone seed supplied by the parent during the same render flow. */
  initialPhone?: string;
  /** Notify the parent on every keystroke. This must not persist the value. */
  onPhoneInputChange?: (e164: string) => void;
}

/** Parse an E.164 number into (country, national-digits). Falls back to AU. */
function seedFromE164(e164?: string): { country: CountryCode; national: string } {
  if (!e164) return { country: 'AU', national: '' };
  try {
    const parsed = parsePhoneNumber(e164);
    if (parsed?.country && parsed.nationalNumber) {
      return { country: parsed.country as CountryCode, national: String(parsed.nationalNumber) };
    }
  } catch { /* fall through */ }
  // Best-effort: strip leading '+' and digits-only.
  const digits = e164.replace(/\D/g, '');
  return { country: 'AU', national: digits };
}

export function PhoneAuthInput({
  onSubmit,
  isLoading,
  mode = 'signup',
  buttonText,
  // Temporarily disabled until WhatsApp Business sender is approved on Twilio Verify.
  // Re-enable by passing showWhatsAppOption={true} or flipping this default back.
  showWhatsAppOption = false,
  initialPhone,
  onPhoneInputChange,
}: PhoneAuthInputProps) {
  const { t } = useTranslation("common");
  const defaultButtonText = t("web.auth.continue");
  const finalButtonText = buttonText || defaultButtonText;
  // Lazy init from the parent-restored E.164 so the user sees their number
  // immediately on mount - no flicker, no race with auto-IP detection.
  const seeded = useMemo(() => seedFromE164(initialPhone), [initialPhone]);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(seeded.country);
  const [phoneNumber, setPhoneNumber] = useState(seeded.national);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Track whether the user has typed; once they have, do NOT clobber their
  // input with the IP geo-detected default country.
  const userTouched = useRef<boolean>(!!seeded.national);
  const lastSeededPhone = useRef(initialPhone || '');

  const selectedOption = useMemo(
    () => ALL_COUNTRIES.find(c => c.code === selectedCountry) || ALL_COUNTRIES[0],
    [selectedCountry]
  );

  // Auto-detect country on mount - only when we have no restored / typed value.
  useEffect(() => {
    if (userTouched.current) return;
    const detect = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(5000) });
        const data = await res.json();
        if (data?.country_code && ALL_COUNTRIES.some(c => c.code === data.country_code)) {
          if (userTouched.current) return; // user typed during the fetch
          setSelectedCountry(data.country_code as CountryCode);
        }
      } catch {
        // Keep default
      }
    };
    detect();
  }, []);

  useEffect(() => {
    const nextInitial = initialPhone || '';
    if (!nextInitial || nextInitial === lastSeededPhone.current || userTouched.current) return;
    const nextSeed = seedFromE164(nextInitial);
    lastSeededPhone.current = nextInitial;
    setSelectedCountry(nextSeed.country);
    setPhoneNumber(nextSeed.national);
    setError('');
    userTouched.current = !!nextSeed.national;
  }, [initialPhone]);

  useEffect(() => {
    if (!open) inputRef.current?.focus();
  }, [open]);


  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value.replace(/\D/g, "");
    userTouched.current = true;
    setPhoneNumber(next);
    setError("");
    onPhoneInputChange?.(next ? normalizePhoneForCountry(next, selectedCountry) || `+${selectedOption.dialCode}${next}` : "");
  };


  const submitWithChannel = async (channel: 'sms' | 'whatsapp') => {
    if (!phoneNumber) {
      setError(t("web.auth.errEnterPhone"));
      return;
    }
    const fullNumber = normalizePhoneForCountry(phoneNumber, selectedCountry);
    try {
      if (!fullNumber || !isValidPhoneNumber(fullNumber)) {
        setError(t("web.auth.errValidPhone"));
        return;
      }
      const parsed = parsePhoneNumber(fullNumber);
      if (!parsed) {
        setError(t("web.auth.errValidPhone"));
        return;
      }
      await onSubmit(parsed.format("E.164"), channel);
    } catch {
      setError(t("web.auth.errValidPhone"));
    }
  };

  const validateAndSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitWithChannel('sms');
  };

  return (
    <form onSubmit={validateAndSubmit} className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          {t("web.auth.phoneLabel")}
        </label>
        <div className="flex gap-2">
          {/* Country Selector */}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-[140px] shrink-0 justify-between px-3"
                type="button"
              >
                <div className="flex items-center gap-2 truncate">
                  <img
                    src={getFlagSvgUrl(iso2ToFlagEmoji(selectedCountry))}
                    alt=""
                    className="w-5 h-4 object-cover rounded-sm shrink-0"
                  />
                  <span>+{selectedOption.dialCode}</span>
                </div>
                <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0 z-50" align="start">
              <Command>
                <CommandInput placeholder={t("web.auth.searchCountry")} />
                <CommandList>
                  <CommandEmpty>{t("web.auth.noCountryFound")}</CommandEmpty>
                  <CommandGroup heading={t("web.auth.popular")}>
                    {POPULAR_COUNTRIES.map((country) => (
                      <CommandItem
                        key={country.code}
                        value={`${country.name} +${country.dialCode}`}
                        onSelect={() => {
                          setSelectedCountry(country.code);
                          setOpen(false);
                        }}
                      >
                        <img
                          src={getFlagSvgUrl(iso2ToFlagEmoji(country.code))}
                          alt=""
                          className="w-5 h-4 object-cover rounded-sm mr-2 shrink-0"
                        />
                        <span className="truncate">{country.name}</span>
                        <span className="ml-auto text-muted-foreground text-xs">+{country.dialCode}</span>
                        <Check className={cn("ml-2 h-4 w-4 shrink-0", selectedCountry === country.code ? "opacity-100" : "opacity-0")} />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandGroup heading={t("web.auth.allCountries")}>
                    {OTHER_COUNTRIES.map((country) => (
                      <CommandItem
                        key={country.code}
                        value={`${country.name} +${country.dialCode}`}
                        onSelect={() => {
                          setSelectedCountry(country.code);
                          setOpen(false);
                        }}
                      >
                        <img
                          src={getFlagSvgUrl(iso2ToFlagEmoji(country.code))}
                          alt=""
                          className="w-5 h-4 object-cover rounded-sm mr-2 shrink-0"
                        />
                        <span className="truncate">{country.name}</span>
                        <span className="ml-auto text-muted-foreground text-xs">+{country.dialCode}</span>
                        <Check className={cn("ml-2 h-4 w-4 shrink-0", selectedCountry === country.code ? "opacity-100" : "opacity-0")} />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Phone Input */}
          <div className="relative flex-1">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={inputRef}
              type="tel"
              placeholder={t("web.auth.phonePlaceholder")}
              value={phoneNumber}
              onChange={handlePhoneChange}
              className="pl-10"
              disabled={isLoading}
              inputMode="numeric"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
        </div>
        
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </div>

      <Button
        type="submit"
        className="w-full h-12 text-base"
        disabled={isLoading || !phoneNumber}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            {t("web.auth.sendingCode")}
          </>
        ) : (
          finalButtonText
        )}
      </Button>

      {showWhatsAppOption && (
        <Button
          type="button"
          variant="outline"
          className="w-full h-12 text-base border-[#25D366]/40 text-[#128C7E] hover:bg-[#25D366]/5"
          disabled={isLoading || !phoneNumber}
          onClick={() => submitWithChannel('whatsapp')}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 mr-2 fill-current" aria-hidden="true">
            <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.667 5.452l-.999 3.648 3.821-.999z"/>
          </svg>
          {t("web.auth.verifyWhatsApp")}
        </Button>
      )}

      <p className="text-xs text-center text-muted-foreground">
        {t("web.auth.sixDigitHelper")}
      </p>
    </form>
  );
}
